import {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {Box, Typography, Fade, Stack, Chip, Alert} from '@mui/material';
import {useFsCreateCtx} from '@/pages/components/audit/finance-statement/create-stepper/context';
import {useCompanyAnnualForm} from '@/pages/components/audit/finance-statement/create-stepper/hooks/useCompanyAnnualForm';
import {FormSkeleton} from '@/pages/components/audit/finance-statement/create-stepper/components/FormSkeleton';
import EpAuditOpinion from '@/components/company-info/company-annual/sub-content/EpAuditOpinion';
import EpCompanyNameAuditorChanges
    from '@/components/company-info/company-annual/sub-content/EpCompanyNameAuditorChanges';
import EpCurrencyExchange from '@/components/company-info/company-annual/sub-content/EpCurrencyExchange';
import EpTax from '@/components/company-info/company-annual/sub-content/EpTax';
import EpAuditStaffs from '@/components/company-info/company-annual/sub-content/EpAuditStaffs';
import EpOther from '@/components/company-info/company-annual/sub-content/EpOther';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {processAliases} from '@/services/auditService/auditFsAnnualService';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {SnackbarField} from '@/components/common';
import {buildFormerAuditorInitialFields, sanitizeFormerAuditorAddress} from '@/utils/formerAuditorFields';

export default function StepCCaAuditConfig() {
    const {state, dispatch} = useFsCreateCtx();
    const setAnnualCache = useCompanyAnnualStore((s) => s.setAnnual);
    const [showContent, setShowContent] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<{
        qualifyOpinion?: boolean;
        referrer?: boolean;
        inCharge?: boolean;
    }>({});
    const [inCorpDate, setInCorpDate] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error';
    }>({open: false});

    const annualId = state.annualId || ((state.selectedAnnual as any)?.annualId ?? (state.selectedAnnual as any)?.id);
    const companyId = Number((state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id);

    // 使用 ref 保存最新的 form 数据
    const formRef = useRef<CompanyAnnual | null>(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const [restoredData, setRestoredData] = useState<any>(null);
    const restoredFromDraftRef = useRef(false);

    const annualFromStore = useCompanyAnnualStore((s) => s.byAnnualId[Number(annualId || 0)]);

    const handleLoadComplete = useCallback(() => {
        setTimeout(() => setShowContent(true), 100);
    }, []);

    const handleSaveComplete = useCallback(
        (data: CompanyAnnual) => {
            if (data && data.annualId && data.annualId > 0) {
                dispatch({type: 'set_annual_id', payload: data.annualId});
            }
        },
        [dispatch]
    );

    const {form, loading, error, updateForm, forceSave, saveData} = useCompanyAnnualForm({
        annualId: annualId && Number(annualId) > 0 ? Number(annualId) : undefined,
        companyId: isRestoring ? undefined : (companyId ? Number(companyId) : undefined), // 恢复时不传 companyId
        autoSave: false,
        initialData: restoredData,
        onLoadComplete: handleLoadComplete,
        onSaveComplete: handleSaveComplete,
        onCreateBlocked: (message) => setSnackbar({open: true, message, severity: 'error'}),
        createGuardFlow: 'audit',
        createFlowType: 1,
    });

    useEffect(() => {
        restoredFromDraftRef.current = false;
    }, [state.draftData]);

    useEffect(() => {
        if (restoredFromDraftRef.current) {
            return;
        }
        if (!state.draftData?.formData) {
            return;
        }
        const currentCompanyId = Number((state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id ?? 0);
        if (Number(state.draftData.companyId || 0) !== Number(currentCompanyId || 0)) {
            return;
        }

        const expectedAnnualId =
            state.annualId ??
            ((state.selectedAnnual as any)?.annualId ?? (state.selectedAnnual as any)?.id ?? null);
        const draftAnnualId = Number((state.draftData as any)?.annualId ?? 0) || null;

        if (!state.isCreatingAnnual) {
            if (!draftAnnualId || draftAnnualId <= 0) {
                return;
            }
            if (expectedAnnualId && Number(expectedAnnualId) > 0 && Number(expectedAnnualId) !== Number(draftAnnualId)) {
                return;
            }
        }

        restoredFromDraftRef.current = true;
        setIsRestoring(true);

        let effectiveAnnualId = expectedAnnualId ?? draftAnnualId ?? null;
        if (!effectiveAnnualId && state.isCreatingAnnual) {
            effectiveAnnualId = -Date.now();
        }
        if (effectiveAnnualId !== state.annualId) {
            dispatch({type: 'set_annual_id', payload: effectiveAnnualId});
        }

        const dataWithTempId = { ...(state.draftData.formData as any), annualId: effectiveAnnualId };
        setAnnualCache(dataWithTempId as any);
        setRestoredData(dataWithTempId);

        setTimeout(() => {
            setIsRestoring(false);
        }, 100);
    }, [dispatch, setAnnualCache, state.draftData, state.annualId, state.isCreatingAnnual, state.selectedAnnual, state.selectedCompany]);

    // 更新 formRef 和 inCorpDate
    useEffect(() => {
        formRef.current = form;

        // 优先从 form 中提取 inCorpDate
        if (form) {
            const formData = form as any;
            if (formData.inCorpDate && formData.inCorpDate !== inCorpDate) {
                setInCorpDate(formData.inCorpDate);
                return;
            }
        }

        // 如果 form 中没有 inCorpDate，尝试从 selectedCompany 中获取
        if (!inCorpDate && state.selectedCompany) {
            const companyInCorpDate = (state.selectedCompany as any)?.inCorpDate;
            if (companyInCorpDate) {
                setInCorpDate(companyInCorpDate);
            }
        }
    }, [form, inCorpDate, state.selectedCompany]);

    const applyFormPatch = useCallback(
        (patch: Partial<CompanyAnnual>) => {
            updateForm(patch);
            formRef.current = ({
                ...((formRef.current as any) || (annualFromStore as any) || ({} as any)),
                ...(patch as any),
            } as any) as CompanyAnnual;

            const id = Number(annualId || 0);
            if (!id) return;
            setAnnualCache({
                ...((annualFromStore as any) || ({annualId: id, companyId: Number(companyId || 0)} as any)),
                annualId: id,
                ...(patch as any),
            } as any);
        },
        [annualFromStore, annualId, companyId, setAnnualCache, updateForm]
    );

    // Auto-fill from Content Sheet import (self-healing: re-applies if form is overwritten)
    useEffect(() => {
        const cd = state.contentData;
        if (!cd || !form) return;

        const patch: Partial<CompanyAnnual> = {};

        // EpAuditOpinion — only patch if form value differs from content
        if (cd.qualifyOpinion != null && (form as any).qualifyOpinion !== cd.qualifyOpinion) patch.qualifyOpinion = cd.qualifyOpinion;
        if (cd.firstAudit != null && (form as any).firstAudit !== cd.firstAudit) patch.firstAudit = cd.firstAudit;

        // EpCompanyNameAuditorChanges
        if (cd.isChangeName != null && (form as any).isChangeName !== cd.isChangeName) patch.isChangeName = cd.isChangeName;
        if (cd.oldCompanyName && (form as any).oldCompanyName !== cd.oldCompanyName) patch.oldCompanyName = cd.oldCompanyName;
        if (cd.changeEffectiveDate && (form as any).changeEffectiveDate !== cd.changeEffectiveDate) patch.changeEffectiveDate = cd.changeEffectiveDate;
        if (cd.formerAuditor && (form as any).formerAuditor !== cd.formerAuditor) patch.formerAuditor = cd.formerAuditor;
        const contentAddress = sanitizeFormerAuditorAddress(cd.formerAuditor, cd.addressOfFormerAuditor);
        if (contentAddress && (form as any).addressOfFormerAuditor !== contentAddress) {
            patch.addressOfFormerAuditor = contentAddress;
        }
        if (cd.professionalClearance && (form as any).professionalClearance !== cd.professionalClearance) patch.professionalClearance = cd.professionalClearance;

        // EpCurrencyExchange
        if (cd.presentationCurrency && (form as any).presentationCurrency !== cd.presentationCurrency) patch.presentationCurrency = cd.presentationCurrency;
        if (cd.exchangeRate != null && (form as any).exchangeRate !== cd.exchangeRate) patch.exchangeRate = cd.exchangeRate;
        if (cd.functionalCurrency && (form as any).functionalCurrency !== cd.functionalCurrency) patch.functionalCurrency = cd.functionalCurrency;

        // Dividend fields
        if (cd.dividendPaid != null && (form as any).dividendPaid !== cd.dividendPaid) patch.dividendPaid = cd.dividendPaid;
        if (cd.dividendCurrency && (form as any).dividendCurrency !== cd.dividendCurrency) patch.dividendCurrency = cd.dividendCurrency;
        if (cd.amountPerEachShare != null && (form as any).amountPerEachShare !== cd.amountPerEachShare) patch.amountPerEachShare = cd.amountPerEachShare;
        if (cd.totalDividendAmount != null && (form as any).totalDividendAmount !== cd.totalDividendAmount) patch.totalDividendAmount = cd.totalDividendAmount;

        // EpTax
        if (cd.twoTierTaxThisYear != null && (form as any).twoTierTaxThisYear !== cd.twoTierTaxThisYear) patch.twoTierTaxThisYear = cd.twoTierTaxThisYear;
        if (cd.twoTierTaxLastYear != null && (form as any).twoTierTaxLastYear !== cd.twoTierTaxLastYear) patch.twoTierTaxLastYear = cd.twoTierTaxLastYear;
        if (cd.taxLossBF != null && (form as any).taxLossBF !== cd.taxLossBF) patch.taxLossBF = cd.taxLossBF;

        // EpOther
        if (cd.referrer && (form as any).referrer !== cd.referrer) patch.referrer = cd.referrer;
        if (cd.inCharge && (form as any).inCharge !== cd.inCharge) patch.inCharge = cd.inCharge;

        if (Object.keys(patch).length > 0) {
            applyFormPatch(patch);
        }
    }, [state.contentData, form, applyFormPatch]);

    const handleAuditOpinionChange = useCallback(
        (patch: Partial<CompanyAnnual>) => {
            applyFormPatch(patch);
            if ((patch as any).qualifyOpinion !== undefined) {
                setFieldErrors((prev) => ({...prev, qualifyOpinion: false}));
            }
        },
        [applyFormPatch]
    );

    const handleOtherChange = useCallback(
        (patch: Partial<CompanyAnnual>) => {
            applyFormPatch(patch);
            if ((patch as any).referrer !== undefined) {
                setFieldErrors((prev) => ({...prev, referrer: false}));
            }
            if ((patch as any).inCharge !== undefined) {
                setFieldErrors((prev) => ({...prev, inCharge: false}));
            }
        },
        [applyFormPatch]
    );

    // 计算 inCorpDate 和 periodFrom 之间的月份间隔
    const monthsBetweenIncorpAndPeriod = useMemo(() => {
        if (!inCorpDate || !form?.periodFrom) {
            return null;
        }

        // 解析 DD/MM/YYYY 格式
        const parseDMY = (dateStr: string): Date | null => {
            const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
            if (!match) return null;
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            return new Date(year, month, day);
        };

        const incorpDate = parseDMY(inCorpDate);
        const periodFromDate = parseDMY(form.periodFrom);

        if (!incorpDate || !periodFromDate) return null;

        // 计算月份差异
        const yearDiff = periodFromDate.getFullYear() - incorpDate.getFullYear();
        const monthDiff = periodFromDate.getMonth() - incorpDate.getMonth();
        const dayDiff = periodFromDate.getDate() - incorpDate.getDate();

        let months = yearDiff * 12 + monthDiff;
        // 如果天数差异为负，减去一个月
        if (dayDiff < 0) {
            months--;
        }

        return months;
    }, [inCorpDate, form?.periodFrom]);

    // 根据月份间隔决定显示哪些字段
    // <= 18个月: 显示 First Time Audit，不显示 Last Year two tired tax
    // > 18个月: 不显示 First Time Audit，显示 Last Year two tired tax
    const showFirstAudit = monthsBetweenIncorpAndPeriod !== null && monthsBetweenIncorpAndPeriod <= 18;
    const showLastYearTwoTierTax = monthsBetweenIncorpAndPeriod !== null && monthsBetweenIncorpAndPeriod > 18;

    // Auto-detect first audit: periodFrom === inCorpDate
    const isFirstAuditForced = useMemo(() => {
        if (!inCorpDate || !form?.periodFrom) return false;
        return inCorpDate === form.periodFrom;
    }, [inCorpDate, form?.periodFrom]);

    // Auto-set firstAudit = 1 when periodFrom === inCorpDate
    useEffect(() => {
        if (
            isFirstAuditForced
            && form
            && (
                (form as any).firstAudit !== 1
                || (form as any).lastYearPeriodFrom
                || (form as any).lastYearPeriodTo
            )
        ) {
            applyFormPatch({
                firstAudit: 1,
                lastYearPeriodFrom: null,
                lastYearPeriodTo: null,
            } as Partial<CompanyAnnual>);
        }
    }, [isFirstAuditForced, form?.firstAudit, form?.lastYearPeriodFrom, form?.lastYearPeriodTo, applyFormPatch]);

    // 使用 useCallback 包装 beforeNext 函数，参考 StepBCaBasicInfo
    const handleBeforeNext = useCallback(async (clearDraft = true) => {
        try {
            dispatch({type: 'set_is_saving', payload: true});

            // Back 时只做暂存，不保存到后端，也不做必填校验（允许用户返回上一步继续填写）
            if (!clearDraft) {
                const currentCompanyId = Number((state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id ?? 0);

                const mergedDraft: CompanyAnnual | null = formRef.current
                    ? (({
                        ...(state.draftData?.formData || {}),
                        ...formRef.current,
                    } as any) as CompanyAnnual)
                    : ((state.draftData?.formData as any) || null);

                const {annualId: _draftAnnualId, ...draftFormData} = (mergedDraft as any) || ({} as any);

                const draftAnnualIdCandidate =
                    state.annualId ??
                    (state.draftData as any)?.annualId ??
                    (formRef.current as any)?.annualId ??
                    null;
                const draftAnnualId = draftAnnualIdCandidate && Number(draftAnnualIdCandidate) > 0 ? Number(draftAnnualIdCandidate) : null;

                dispatch({
                    type: 'save_draft',
                    payload: {
                        companyId: currentCompanyId,
                        annualId: draftAnnualId,
                        formData: draftFormData,
                    },
                });
                dispatch({type: 'set_is_saving', payload: false});
                return true;
            }

            // 确保 annualId 存在于 Context 中
            let currentAnnualId = state.annualId;
            if (!currentAnnualId) {
                // 尝试从 selectedAnnual 或 form 中获取
                const extractedId = (state.selectedAnnual as any)?.annualId ?? (state.selectedAnnual as any)?.id ?? formRef.current?.annualId;
                if (extractedId && extractedId > 0) {
                    currentAnnualId = extractedId;
                    dispatch({type: 'set_annual_id', payload: extractedId});
                }
            }

            // Next 场景下：校验/保存应基于 merged 数据（draftData + current form）
            const draftForValidate = ((state.draftData?.formData as any) || {}) as CompanyAnnual;
            const currentForValidate = ((formRef.current as any) || {}) as CompanyAnnual;
            // IMPORTANT: 不要让 current 的 null/undefined 覆盖 draft（否则会出现 UI 有值但校验读到 null）
            const currentNonNull = Object.fromEntries(
                Object.entries(currentForValidate as any).filter(([, v]) => v !== null && v !== undefined)
            ) as Partial<CompanyAnnual>;
            const mergedForValidate = ({
                ...(draftForValidate as any),
                ...(currentNonNull as any),
            } as any) as CompanyAnnual;

            const qualifyOpinionRaw = (mergedForValidate as any)?.qualifyOpinion;
            const qualifyOpinionNum =
                qualifyOpinionRaw === '' || qualifyOpinionRaw === null || qualifyOpinionRaw === undefined
                    ? undefined
                    : Number(qualifyOpinionRaw);

            // ========== 第一步：先校验数据 ==========
            const errors: typeof fieldErrors = {};

            // qualifyOpinion 必须是 0 或 1（0 是有效值）
            if (qualifyOpinionNum !== 0 && qualifyOpinionNum !== 1) {
                errors.qualifyOpinion = true;
            }

            // referrer 必须是非空字符串
            if (!mergedForValidate?.referrer || String(mergedForValidate.referrer).trim() === '') {
                errors.referrer = true;
            }

            // inCharge 必须是非空字符串
            if (!mergedForValidate?.inCharge || String(mergedForValidate.inCharge).trim() === '') {
                errors.inCharge = true;
            }

            // 如枟有校验错误，立即返回，不进行保存
            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors);
                dispatch({type: 'set_is_saving', payload: false});
                return false;
            }

            // ========== 第二步：校验通过后再保存数据 ==========
            const mergedData: CompanyAnnual | null = formRef.current
                ? ({
                    ...(state.draftData?.formData || {}),
                    ...(Object.fromEntries(
                        Object.entries((formRef.current as any) || {}).filter(([, v]) => v !== null && v !== undefined)
                    ) as any),
                } as any as CompanyAnnual)
                : null;

            if (!mergedData) {
                dispatch({type: 'set_is_saving', payload: false});
                return false;
            }

            const dataForSave: any = {...(mergedData as any)};
            // draft 过程中不需要 annualId（尤其是临时负数/0），由后端回传
            if (!dataForSave.annualId || Number(dataForSave.annualId) <= 0) {
                delete dataForSave.annualId;
            }
            const savedData = await saveData(dataForSave as any);
            if (savedData) {
                formRef.current = savedData;
                if (savedData.annualId && savedData.annualId > 0) {
                    dispatch({type: 'set_annual_id', payload: savedData.annualId});
                }
            }

            // CompanyAnnual 保存成功后，如有 importToken 则触发别名持久化（方案1）
            const finalAnnualId = savedData?.annualId && savedData.annualId > 0
                ? savedData.annualId
                : (currentAnnualId && Number(currentAnnualId) > 0 ? Number(currentAnnualId) : null);
            if (state.importToken && companyId && finalAnnualId && finalAnnualId > 0) {
                try {
                    await processAliases(companyId, finalAnnualId, state.importToken);
                } catch (e) {
                    console.warn('[StepC] processAliases failed (non-blocking):', e);
                }
            }

            // 保存成功后将最新数据写回暂存，支持 Back 回到 Step 2/3 时直接恢复
            const {annualId: _savedAnnualId, ...savedFormData} = ((savedData || mergedData) as any) || ({} as any);
            dispatch({
                type: 'save_draft',
                payload: {
                    companyId: Number((state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id ?? 0),
                    annualId: savedData?.annualId && savedData.annualId > 0 ? savedData.annualId : null,
                    formData: savedFormData,
                },
            });

            // 清除错误状态
            setFieldErrors({});

            dispatch({type: 'set_is_saving', payload: false});
            return true;
        } catch (e) {
            console.error('[StepCCaAuditConfig] Failed to save before next:', e);
            dispatch({type: 'set_is_saving', payload: false});
            return false;
        }
    }, [dispatch, saveData, companyId, state.annualId, state.selectedAnnual, state.selectedCompany, state.draftData, state.importToken]);

    useEffect(() => {
        dispatch({
            type: 'set_before_next',
            payload: handleBeforeNext,
        });

        return () => {
            dispatch({type: 'set_before_next', payload: undefined});
        };
    }, [dispatch, handleBeforeNext]);

    const hideFormerAuditor = Number((form as any)?.qualifyOpinion) === 1;

    const companyNameAuditorInitialData = useMemo(() => {
        const cd = state.contentData;
        if (!form && !cd) return undefined;

        const formerAuditorFields = buildFormerAuditorInitialFields(form, cd);

        return {
            isChangeName: form?.isChangeName ?? cd?.isChangeName ?? undefined,
            oldCompanyName: form?.oldCompanyName || cd?.oldCompanyName || null,
            changeEffectiveDate: form?.changeEffectiveDate || cd?.changeEffectiveDate || null,
            changeOfFinancialYearEnd: form?.changeOfFinancialYearEnd ?? cd?.changeOfFinancialYearEnd ?? undefined,
            dateOfBoardResolution: form?.dateOfBoardResolution || cd?.dateOfBoardResolution || null,
            reasonToChange: form?.reasonToChange || cd?.reasonToChange || null,
            ...formerAuditorFields,
        };
    }, [form, state.contentData]);

    if (!annualId && !state.isCreatingAnnual) {
        return (
            <Box sx={{p: 2}}>
                <Alert severity="info">Please complete Step 2 first.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{pt: 1}}>
            {/* Creating Mode Header */}
            {state.isCreatingAnnual && (
                <Box sx={{mb: 3}}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Creating New Annual - Step 2/2
                        </Typography>
                        <Chip label="Draft" color="warning" size="small"/>
                    </Stack>
                    <Alert severity="info" sx={{mt: 1}}>
                        Continue filling in the audit configuration. Your data is being automatically saved.
                    </Alert>
                </Box>
            )}

            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                    Audit Configuration
                </Typography>
                {state.isSaving && (
                    <Chip label="Saving..." color="primary" size="small" variant="outlined"/>
                )}
            </Stack>

            {loading ? (
                <FormSkeleton rows={6} showTitle/>
            ) : (
                <Fade in={showContent} timeout={300}>
                    <Box>
                        <EpAuditOpinion
                            annualId={Number(annualId || 0)}
                            onChange={handleAuditOpinionChange}
                            showFirstAudit={showFirstAudit}
                            forceFirstAudit={isFirstAuditForced}
                            externalErrors={{
                                qualifyOpinion: fieldErrors.qualifyOpinion,
                            }}
                            disableAccordion={true}
                            initialData={form ? {
                                qualifyOpinion: state.contentData?.qualifyOpinion ?? form.qualifyOpinion ?? undefined,
                                firstAudit: state.contentData?.firstAudit ?? form.firstAudit ?? undefined,
                            } : null}
                        />
                        <EpCompanyNameAuditorChanges
                            annualId={Number(annualId || 0)}
                            onChange={(patch) => applyFormPatch(patch)}
                            disableAccordion={true}
                            hideFormerAuditor={hideFormerAuditor}
                            initialData={companyNameAuditorInitialData}
                        />
                        <EpCurrencyExchange
                            annualId={Number(annualId || 0)}
                            onChange={(patch) => applyFormPatch(patch)}
                            disableAccordion={true}
                        />
                        <EpTax
                            annualId={Number(annualId || 0)}
                            onChange={(patch) => applyFormPatch(patch)}
                            showLastYearTwoTierTax={showLastYearTwoTierTax}
                            disableAccordion={true}
                            hideAssessableAndTaxLossFields={true}
                        />
                        <EpAuditStaffs
                            annualId={Number(annualId || 0)}
                            onChange={(patch) => applyFormPatch(patch)}
                            disableAccordion={true}
                        />
                        <EpOther
                            annualId={Number(annualId || 0)}
                            onChange={handleOtherChange}
                            disableAccordion={true}
                            initialData={form ? {
                                referrer: state.contentData?.referrer || form.referrer || null,
                                inCharge: state.contentData?.inCharge || form.inCharge || null,
                            } : null}
                            externalErrors={{
                                referrer: fieldErrors.referrer,
                                inCharge: fieldErrors.inCharge,
                            }}
                        />
                    </Box>
                </Fade>
            )}
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message || ''}
                severity={snackbar.severity}
                onClose={() => setSnackbar((s) => ({...s, open: false}))}
            />
        </Box>
    );
}
